/**
 * UsageDashboard — Itinerary usage bar widget.
 *
 * Accepts usage data as props (preferred — avoids a duplicate API call).
 * The parent should pass { usageSummary, loadingUsage } from useUser().
 */

import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { AlertCircle, TrendingUp, Calendar, Zap, ArrowUpRight } from 'lucide-react';

const getUsageStatus = (percentage) => {
  if (percentage >= 100) return { status: 'exceeded', color: 'red',    icon: AlertCircle };
  if (percentage >= 80)  return { status: 'warning',  color: 'yellow', icon: AlertCircle };
  if (percentage >= 60)  return { status: 'moderate', color: 'blue',   icon: TrendingUp  };
  return                        { status: 'good',     color: 'green',  icon: Zap         };
};

const COLOR_CLASSES = {
  red:    { bg: 'bg-red-50',    border: 'border-red-200',    text: 'text-red-600',    progress: 'bg-red-500'    },
  yellow: { bg: 'bg-yellow-50', border: 'border-yellow-200', text: 'text-yellow-600', progress: 'bg-yellow-500' },
  blue:   { bg: 'bg-blue-50',   border: 'border-blue-200',   text: 'text-blue-600',   progress: 'bg-blue-500'   },
  green:  { bg: 'bg-green-50',  border: 'border-green-200',  text: 'text-green-600',  progress: 'bg-green-500'  },
};

/**
 * @param {{ usageSummary: object|null, loadingUsage: boolean, className: string }} props
 */
const UsageDashboard = ({ usageSummary, loadingUsage = false, className = '' }) => {
  const navigate = useNavigate();

  if (loadingUsage) {
    return (
      <div className={`usage-dashboard ${className}`}>
        <div className="p-6 bg-white rounded-lg border border-gray-200">
          <div className="flex items-center space-x-3">
            <div className="w-8 h-8 bg-gray-200 rounded-full animate-pulse" />
            <div className="flex-1">
              <div className="h-4 bg-gray-200 rounded w-1/4 mb-2 animate-pulse" />
              <div className="h-3 bg-gray-200 rounded w-1/2 animate-pulse" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Nothing to show if data isn't available (non-fatal)
  if (!usageSummary) return null;

  const usage           = usageSummary;
  const usagePercentage = usage.usage_percentage ?? 0;
  const statusInfo      = getUsageStatus(usagePercentage);
  const StatusIcon      = statusInfo.icon;
  const colors          = COLOR_CLASSES[statusInfo.color];
  const isUnlimited     = usage.monthly_limit === -1;
  const remaining       = isUnlimited ? Infinity : (usage.monthly_limit - usage.monthly_itineraries);

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className={`usage-dashboard ${className}`}
    >
      <div className={`p-3 sm:p-4 rounded-lg border ${colors.bg} ${colors.border}`}>
        {/* Header row */}
        <div className="flex items-start justify-between mb-2">
          <div className="flex items-center space-x-2">
            <div className={`p-1.5 rounded-lg ${colors.bg}`}>
              <Calendar className={`w-4 h-4 ${colors.text}`} aria-hidden="true" />
            </div>
            <div>
              <h3 className="text-xs font-semibold text-gray-900">Itinerary Usage</h3>
              {usage.plan_name && (
                <p className="text-[10px] text-gray-600">
                  Current plan:{' '}
                  <span className="font-medium capitalize">{usage.plan_name.replace(/_/g, ' ')}</span>
                </p>
              )}
            </div>
          </div>
          <StatusIcon className={`w-4 h-4 ${colors.text}`} aria-hidden="true" />
        </div>

        {/* Count + progress */}
        <div className="mb-2">
          <div className="flex justify-between items-baseline mb-1.5">
            <span className="text-xl font-bold text-gray-900">
              {usage.monthly_itineraries}
              <span className="ml-1 text-xs font-normal text-gray-600">
                / {isUnlimited ? '∞' : usage.monthly_limit}
              </span>
            </span>
            <span className={`text-xs font-medium ${colors.text}`}>
              {isUnlimited ? 'Unlimited' : `${Math.round(usagePercentage)}%`}
            </span>
          </div>

          {!isUnlimited && (
            <div className="w-full h-1.5 bg-gray-200 rounded-full" role="progressbar" aria-valuenow={Math.round(usagePercentage)} aria-valuemin={0} aria-valuemax={100}>
              <motion.div
                initial={{ width: 0 }}
                animate={{ width: `${Math.min(usagePercentage, 100)}%` }}
                transition={{ duration: 0.5, ease: 'easeOut' }}
                className={`h-1.5 rounded-full ${colors.progress}`}
              />
            </div>
          )}
        </div>

        {/* Status message */}
        <p className="text-[10px] text-gray-600">
          {usage.can_create
            ? `You can create ${isUnlimited ? 'unlimited' : remaining} more itinerar${remaining === 1 ? 'y' : 'ies'} this month`
            : 'Monthly limit reached — upgrade to create more itineraries.'}
        </p>

        {/* Upgrade CTA — show at 80%+ */}
        {usagePercentage >= 80 && !isUnlimited && (
          <button
            onClick={() => navigate('/agent/settings?tab=subscription')}
            className="flex justify-center items-center mt-2 px-3 py-1.5 w-full space-x-2 text-xs font-medium text-white bg-blue-600 rounded-lg transition-colors hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1"
          >
            <span>Upgrade Plan</span>
            <ArrowUpRight className="w-3 h-3" aria-hidden="true" />
          </button>
        )}
      </div>
    </motion.div>
  );
};

export default UsageDashboard;
